import { QUOTE_STATUS_LABELS, SAMPLE_STATUS_LABELS, formatDate, formatILS, formatRelativeDay } from '@takumi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Banknote, CalendarClock, Check, FileText, Package, RefreshCcw } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardHeader } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useInvalidateCrm } from '@/lib/queries';
import type { SalesMyDay } from '@/lib/sales-types';

function Section({ title, icon, count, children }: { title: string; icon: ReactNode; count: number; children: ReactNode }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader title={title} icon={icon} count={count} />
      <div className="divide-y divide-line/70">{children}</div>
    </Card>
  );
}
function Clean({ text }: { text: string }) {
  return <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted"><Check className="size-4 text-success" /> {text}</div>;
}

/** Sales sections of "היום שלי": quotes and samples to follow, reorders, payments. */
export function MyDaySales({ scope }: { scope: 'me' | 'all' }) {
  const { can } = useAuth();
  const invalidate = useInvalidateCrm();
  const { data } = useQuery({ queryKey: ['salesMyDay', scope], queryFn: () => api.get<SalesMyDay>('/my-day/sales', { scope }), enabled: can('sales.read'), refetchInterval: 120_000 });
  const followup = useMutation({
    mutationFn: (orgId: string) => api.post(`/my-day/sales/reorders/${orgId}/followup`, {}),
    onSuccess: () => {
      invalidate();
      toast.success('נקבע פולואפ');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!data) return null;
  return (
    <div className="mt-5 grid gap-5 lg:grid-cols-2">
      <Section title="הצעות מחיר למעקב" icon={<FileText />} count={data.quotes.length}>
        {data.quotes.length ? data.quotes.map((q) => (
          <Link key={q.id} to={`/quotes/${q.id}`} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-sunken/60">
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{q.organizationName}</div>
              <div className="text-xs text-muted"><span className="ltr">{q.number}</span> · נשלחה {q.sentAt ? formatRelativeDay(q.sentAt, new Date(), false) : '—'}</div>
            </div>
            <Badge variant={q.status === 'viewed' ? 'today' : 'tomorrow'}>{QUOTE_STATUS_LABELS[q.status]}</Badge>
            <span className="font-semibold tabular">{formatILS(q.total, { decimals: false })}</span>
          </Link>
        )) : <Clean text="אין הצעות פתוחות" />}
      </Section>
      <Section title="דוגמיות למעקב" icon={<Package />} count={data.samples.length}>
        {data.samples.length ? data.samples.map((s) => (
          <Link key={s.id} to={`/org/${s.organizationId}`} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-sunken/60">
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{s.organizationName}</div>
              <div className="text-xs text-muted">{s.productName ?? 'דוגמית'} · {s.sentAt ? `נשלחה ${formatRelativeDay(s.sentAt, new Date(), false)}` : ''}</div>
            </div>
            <Badge variant="tomorrow">{SAMPLE_STATUS_LABELS[s.status]}</Badge>
          </Link>
        )) : <Clean text="אין דוגמיות שממתינות למשוב" />}
      </Section>
      <Section title="לקוחות להזמנה חוזרת" icon={<RefreshCcw />} count={data.reorders.length}>
        {data.reorders.length ? data.reorders.map((r) => (
          <div key={r.organizationId} className="flex items-center gap-3 px-4 py-2.5 text-sm">
            <div className="min-w-0 flex-1">
              <Link to={`/org/${r.organizationId}`} className="font-semibold hover:underline">{r.organizationName}</Link>
              <div className="text-xs text-muted">{r.estimate.explanation}</div>
            </div>
            <Badge variant={r.estimate.status === 'overdue' ? 'overdue' : 'today'}>
              {r.estimate.status === 'overdue' ? 'באיחור' : 'בקרוב'} · {r.estimate.expectedNextDay && formatDate(r.estimate.expectedNextDay)}
            </Badge>
            {can('crm.write') && !r.openReorderTask && (
              <Button size="sm" variant="secondary" onClick={() => followup.mutate(r.organizationId)} loading={followup.isPending}><CalendarClock /> פולואפ</Button>
            )}
          </div>
        )) : <Clean text="אין לקוחות שצפויים להזמין כעת" />}
      </Section>
      <Section title="תשלומים לטיפול" icon={<Banknote />} count={data.unpaid.length + data.overdueInvoices.length}>
        {data.overdueInvoices.map((i) => (
          <Link key={i.id} to={i.orderId ? `/orders/${i.orderId}` : `/org/${i.organizationId}`} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-sunken/60">
            <div className="min-w-0 flex-1"><div className="font-semibold">{i.organizationName}</div><div className="text-xs text-overdue">חשבונית <span className="ltr">{i.number}</span> — מועד תשלום עבר ({formatDate(i.dueDate)})</div></div>
            <span className="font-semibold tabular">{formatILS(i.total)}</span>
          </Link>
        ))}
        {data.unpaid.map((o) => (
          <Link key={o.id} to={`/orders/${o.id}`} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-sunken/60">
            <div className="min-w-0 flex-1"><div className="font-semibold">{o.organizationName}</div><div className="text-xs text-muted">הזמנה <span className="ltr">{o.number}</span> מ-{formatDate(o.orderDate)} · שולם {formatILS(o.amountPaid)}</div></div>
            <span className="font-semibold text-overdue tabular">{formatILS(o.total - o.amountPaid)}</span>
          </Link>
        ))}
        {!data.unpaid.length && !data.overdueInvoices.length && <Clean text="אין תשלומים בפיגור" />}
      </Section>
    </div>
  );
}
